import {
	faCalculator as fasCalculator,
	faFloppyDisk as fasFloppyDisk,
} from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import type { PureMeasureComponentProps, PureMeasureComponentState } from "@component/PureMeasureComponent";
import { PureMeasureComponent } from "@component/PureMeasureComponent";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { logError } from "@utils/sentry-error";
import autobind from "autobind-decorator";
import type { HTMLInput, ProgressButtonState } from "bm-ui-kit";
import { ProgressButton, PromisePool, findErrorInput, getUrlParameters, isFocusableInput } from "bm-ui-kit";
import type { AnyObject, FormApi } from "final-form";
import createDecorator from "final-form-focus";
import type { ReactNode } from "react";
import React from "react";
import { Container, Form } from "react-bootstrap";
import { Form as FinalForm } from "react-final-form";

type IsetFormValue = (name: string, value: any, justWhenCurrentValueEmpty?: boolean) => void;

interface FormHandlingParams<SearchParams> {
	handleSubmit: (
		event?: Partial<Pick<React.SyntheticEvent, "preventDefault" | "stopPropagation">>,
	) => Promise<AnyObject | undefined> | undefined;
	form: FormApi<SearchParams, Partial<SearchParams>>;
	values: {};
}

export interface FinalFormProps {
	initialValues: {};
	decorators: any[];
	mutators: {};
	onSubmit: (values: any) => void;
}

export interface AbstractSearchFormProps<SearchParams> extends PureMeasureComponentProps {
	className?: string;
	buttonText?: string;
	initRender?: boolean;
	initValues?: SearchParams;

	highlightedBank?: string;

	onSubmit: (searchParams: SearchParams, isSort: boolean) => Promise<void>;
	onExport?: (searchParams: SearchParams) => Promise<void>;
}

export interface AbstractSearchFormState extends PureMeasureComponentState {
	calculateButtonState: ProgressButtonState;
	exportButtonState: ProgressButtonState;
}

export abstract class AbstractSearchForm<
	SearchParams,
	Props extends AbstractSearchFormProps<SearchParams> = AbstractSearchFormProps<SearchParams>,
	State extends AbstractSearchFormState = AbstractSearchFormState,
> extends PureMeasureComponent<Props, State> {
	protected readonly promisePool: PromisePool = new PromisePool();
	private readonly initialValues: SearchParams;
	protected setFormValue: IsetFormValue;
	private firstRender = true;
	private readonly formRef: React.RefObject<HTMLFormElement> = React.createRef();
	private readonly focusOnError: any;

	public constructor(props: Props) {
		super(props);

		this.focusOnError = createDecorator(this.getFormInputs, findErrorInput);
		this.state = this.getInitialState();
		this.initialValues = { ...props.initValues };
	}

	public componentDidMount(): void {}

	public componentWillUnmount(): void {
		this.promisePool.cancel();
	}

	protected getInitialState(): State {
		return {
			calculateButtonState: undefined,
			exportButtonState: undefined,
		} as unknown as State;
	}

	public render(): ReactNode {
		return (
			<FinalForm<SearchParams> {...this.getFinalFormProps()}>
				{({ handleSubmit, form, values }) => {
					const { ...searchParams } = values;
					this.initializeFormHandling({ handleSubmit, form, values });

					return (
						<Form
							id="calculator-container"
							ref={this.formRef}
							className="calculator-container"
							onSubmit={(element) => {
								const { hasValidationErrors, errors } = form.getState();

								if (hasValidationErrors) {
									this.sendEvent("BM_calculationInvalid", {
										...errors,
									});
								}

								return handleSubmit(element);
							}}
							noValidate
						>
							<Container className="search-form">
								{this.renderFields(
									searchParams as unknown as SearchParams,
									form as unknown as FormApi<SearchParams>,
								)}
								{this.renderButtons(
									searchParams as unknown as SearchParams,
									form as unknown as FormApi<SearchParams>,
								)}
							</Container>
						</Form>
					);
				}}
			</FinalForm>
		);
	}

	protected initializeFormHandling({ handleSubmit, form, values }: FormHandlingParams<SearchParams>): void {
		this.setFormValue = (name, value, justWhenCurrentValueEmpty = false) => {
			form.mutators.setValue(name, value, justWhenCurrentValueEmpty);
		};

		if (this.firstRender && getUrlParameters()?.count) {
			this.promisePool.addTimeout(300).then(handleSubmit);
		}
		this.firstRender = false;
	}

	protected getFinalFormProps(): FinalFormProps {
		const initialValues = this.initialValues;
		const decorators = [this.focusOnError];
		const mutators = {
			setValue: ([field, value, justWhenCurrentValueEmpty = false], state, { changeValue }) => {
				if (justWhenCurrentValueEmpty) {
					const currentValue = state.formState.values[field];

					if (
						(typeof value === "object" && Array.isArray(currentValue)) ||
						(typeof value === "string" && currentValue.trim() === "") ||
						(typeof value === "number" && currentValue === null)
					) {
						changeValue(state, field, () => value);
					}
				} else {
					changeValue(state, field, () => value);
				}
			},
		};
		const onSubmit = this.onSubmit;

		return { initialValues, decorators, mutators, onSubmit };
	}
	protected abstract renderFields(values: SearchParams, form: FormApi<SearchParams>): ReactNode;

	protected renderButtons(values: SearchParams, form: FormApi<SearchParams>): ReactNode {
		const { valid } = form.getState();

		return (
			<div className="text-center my-xl-2 my-3">
				{this.renderCalculateButton(valid)}
				<br />
				{this.renderExportButton(valid, values)}
			</div>
		);
	}

	protected renderCalculateButton(_valid: boolean): ReactNode {
		const { buttonText = "Kalkuláció indítása" } = this.props;
		const { calculateButtonState } = this.state;

		return (
			<ProgressButton
				type="submit"
				size="lg"
				state={calculateButtonState}
				icon={<FontAwesomeIcon icon={fasCalculator} />}
				errorChildren="Hiba"
				doneChildren="Kész"
				loadingChildren="Kalkulálás..."
			>
				{buttonText}
			</ProgressButton>
		);
	}

	private renderExportButton(valid: boolean, values: SearchParams): ReactNode {
		const { onExport } = this.props;
		const { exportButtonState } = this.state;

		if (!onExport) {
			return undefined;
		}

		return (
			<ProgressButton
				className="mt-2"
				variant="warning"
				type="submit"
				size="sm"
				state={exportButtonState}
				icon={<FontAwesomeIcon icon={fasFloppyDisk} />}
				errorChildren="Hiba"
				doneChildren="Kész"
				loadingChildren="Exportálás..."
				onClick={() => this.onExport(values)}
				disabled={!valid}
			>
				Export
			</ProgressButton>
		);
	}

	@autobind
	protected onSubmit(values: SearchParams): void {
		const { onSubmit } = this.props;
		const { ...searchParams } = values;

		this.setState({
			calculateButtonState: "loading",
		});

		this.promisePool
			.add(onSubmit(searchParams as unknown as SearchParams, false))
			.then(() => {
				this.setState({
					calculateButtonState: "done",
				});
			})
			.catch((error: Error) => {
				this.setState({
					calculateButtonState: "error",
				});

				logError(error);
			})
			.finally(() => {
				this.promisePool.addTimeout().then(() => {
					this.setState({
						calculateButtonState: undefined,
					});
				});
			});
	}

	@autobind
	private onExport(searchParams: SearchParams): void {
		const { onExport } = this.props;
		this.setState({
			exportButtonState: "loading",
		});

		this.promisePool
			.add(onExport(searchParams))
			.then(() => {
				this.setState({
					exportButtonState: "done",
				});
			})
			.catch((error: Error) => {
				this.setState({
					exportButtonState: "error",
				});

				logError(error);
			})
			.finally(() => {
				this.promisePool.addTimeout().then(() => {
					this.setState({
						exportButtonState: undefined,
					});
				});
			});
	}

	@autobind
	private getFormInputs(): HTMLInput[] {
		const form = this.formRef?.current;

		if (typeof document === "undefined" || !form) {
			return [];
		}

		return Array.prototype.slice.call(form).filter(isFocusableInput);
	}
}
